import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import {
    fetchAdminPublicPage,
    fetchAdminPublicPages,
    updateAdminPublicPage,
} from "../../http/publicPageAPI";

const STATUS_OPTIONS = ["DRAFT", "PUBLISHED", "ARCHIVED"];
const FORMAT_OPTIONS = ["HTML", "MARKDOWN", "JSON"];

const stringifyJson = (value, fallback = {}) => JSON.stringify(value ?? fallback, null, 2);

const formatHtmlSource = (html) => {
    const raw = String(html || "").trim();
    if (!raw) return "";

    const tokens = raw
        .replace(/>\s+</g, "><")
        .match(/<[^>]+>|[^<]+/g) || [];

    const selfClosing = /^(<br\b|<hr\b|<img\b|<input\b|<meta\b|<link\b|<source\b|<area\b|<base\b|<col\b|<embed\b|<param\b|<track\b|<wbr\b)/i;
    const lines = [];
    let level = 0;

    tokens.forEach((token) => {
        const value = token.trim();
        if (!value) return;

        const isClosing = /^<\//.test(value);
        const isComment = /^<!--/.test(value);
        const isSelfClosing = /\/>$/.test(value) || selfClosing.test(value);
        const isOpening = /^<[^/!][^>]*>$/.test(value) && !isSelfClosing;

        if (isClosing) level = Math.max(0, level - 1);
        lines.push(`${"  ".repeat(level)}${value}`);
        if (isOpening && !isComment) level += 1;
    });

    return lines.join("\n");
};

const parseJson = (label, value, fallback) => {
    const raw = String(value || "").trim();
    if (!raw) return fallback;

    try {
        return JSON.parse(raw);
    } catch (error) {
        throw new Error(`${label}: ${error.message}`);
    }
};

const pageToForm = (page) => ({
    ...page,
    pageKey: page.pageKey || "",
    path: page.path || "/",
    slug: page.slug || "",
    lang: page.lang || "en",
    textMode: page.textMode || "",
    type: page.type || "seo_page",
    status: page.status || "DRAFT",
    seoTitle: page.seoTitle || "",
    seoDescription: page.seoDescription || "",
    canonical: page.canonical || "",
    image: page.image || "",
    sourceFile: page.sourceFile || "",
    metadataText: stringifyJson(page.metadata, {}),
    blocks: (page.blocks || []).map((block, index) => ({
        id: block.id,
        sortOrder: block.sortOrder ?? index + 1,
        type: block.type || "content",
        format: block.format || "JSON",
        status: block.status || "PUBLISHED",
        html: (block.format || "JSON") === "HTML" ? formatHtmlSource(block.html || "") : block.html || "",
        markdown: block.markdown || "",
        payloadText: stringifyJson(block.payload, {}),
    })),
    faqText: stringifyJson(page.faqItems || [], []),
    linksText: stringifyJson(page.links || [], []),
});

const compactPageLabel = (page) => `${page.path} (${page.lang})`;

const HtmlEditor = ({ value, onChange }) => {
    const editorRef = useRef(null);
    const [sourceMode, setSourceMode] = useState(true);

    useEffect(() => {
        if (!sourceMode && editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || "";
        }
    }, [value, sourceMode]);

    const commit = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const exec = (command, arg = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, arg);
        commit();
    };

    const makeLink = () => {
        const url = window.prompt("Link URL");
        if (url) exec("createLink", url);
    };

    const insertImageByUrl = () => {
        const imageUrl = window.prompt("Image URL", "/img/");
        if (!imageUrl) return;
        const snippet = `<img src="${imageUrl}" alt="" class="article-inline-image" style="max-width:70%;margin:20px auto;display:block;border-radius:8px;box-shadow:0 2px 6px rgba(0,0,0,0.15);" />`;
        onChange(formatHtmlSource(`${value || ""}\n\n${snippet}`));
    };

    const showSource = () => {
        const html = editorRef.current ? editorRef.current.innerHTML : value;
        onChange(formatHtmlSource(html));
        setSourceMode(true);
    };

    if (sourceMode) {
        return (
            <>
                <div className="d-flex flex-wrap gap-1 mb-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => setSourceMode(false)}>
                        Visual
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={insertImageByUrl}>
                        Insert Image
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => onChange(formatHtmlSource(value))}>
                        Format HTML
                    </Button>
                </div>
                <Form.Control
                    size="sm"
                    as="textarea"
                    rows={18}
                    value={value || ""}
                    onChange={(event) => onChange(event.target.value)}
                    style={{ fontFamily: "Consolas, monospace", fontSize: 13 }}
                />
            </>
        );
    }

    return (
        <>
            <div className="d-flex flex-wrap gap-1 mb-2">
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("bold"); }}>
                    B
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("italic"); }}>
                    I
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("formatBlock", "h2"); }}>
                    H2
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("formatBlock", "h3"); }}>
                    H3
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("formatBlock", "p"); }}>
                    P
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("insertUnorderedList"); }}>
                    UL
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("insertOrderedList"); }}>
                    OL
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); makeLink(); }}>
                    Link
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={insertImageByUrl}>
                    Image
                </Button>
                <Button size="sm" variant="outline-secondary" onMouseDown={(event) => { event.preventDefault(); exec("removeFormat"); }}>
                    Clear
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={showSource}>
                    HTML
                </Button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onBlur={commit}
                className="border rounded bg-white p-3"
                style={{ minHeight: 360, maxHeight: 620, overflow: "auto" }}
            />
        </>
    );
};

const JsonTextarea = ({ label, value, onChange, rows = 8 }) => (
    <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">{label}</Form.Label>
        <Form.Control
            size="sm"
            as="textarea"
            rows={rows}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            style={{ fontFamily: "Consolas, monospace", fontSize: 13 }}
        />
    </Form.Group>
);

const PublicPageEditorAdmin = () => {
    const [filters, setFilters] = useState({
        lang: "all",
        type: "all",
        status: "all",
        q: "",
        limit: 50,
        page: 1,
    });
    const [listData, setListData] = useState({ items: [], total: 0, totalPages: 1 });
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(null);
    const [listLoading, setListLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const typeOptions = useMemo(() => {
        const values = new Set(listData.items.map((item) => item.type).filter(Boolean));
        return ["all", ...Array.from(values).sort()];
    }, [listData.items]);

    const validationIssues = useMemo(() => {
        if (!form) return [];

        const issues = [];
        const jsonTargets = [
            ["Metadata JSON", form.metadataText, {}],
            ["FAQ JSON", form.faqText, []],
            ["Outbound Links JSON", form.linksText, []],
            ...form.blocks.map((block, index) => [`Block ${index + 1} Payload JSON`, block.payloadText, {}]),
        ];

        jsonTargets.forEach(([label, value, fallback]) => {
            try {
                parseJson(label, value, fallback);
            } catch (jsonError) {
                issues.push(jsonError.message);
            }
        });

        if (!form.path || !form.path.startsWith("/")) {
            issues.push("Path must start with /");
        }

        return issues;
    }, [form]);

    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: key === "page" ? value : 1 }));
    };

    const updateForm = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const updateBlock = (index, key, value) => {
        setForm((prev) => ({
            ...prev,
            blocks: prev.blocks.map((block, blockIndex) => (
                blockIndex === index ? { ...block, [key]: value } : block
            )),
        }));
    };

    const addBlock = () => {
        setForm((prev) => ({
            ...prev,
            blocks: [
                ...prev.blocks,
                {
                    sortOrder: prev.blocks.length + 1,
                    type: "content",
                    format: "HTML",
                    status: "PUBLISHED",
                    html: "<p>New content block</p>",
                    markdown: "",
                    payloadText: "{}",
                },
            ],
        }));
    };

    const removeBlock = (index) => {
        setForm((prev) => ({
            ...prev,
            blocks: prev.blocks.filter((_, blockIndex) => blockIndex !== index),
        }));
    };

    const loadList = useCallback(async () => {
        setListLoading(true);
        setError(null);

        try {
            const data = await fetchAdminPublicPages(filters);
            setListData(data);
        } catch (requestError) {
            setError(requestError?.response?.data?.message || requestError.message);
        } finally {
            setListLoading(false);
        }
    }, [filters]);

    const loadPage = async (id) => {
        setSelectedId(id);
        setPageLoading(true);
        setMessage(null);
        setError(null);

        try {
            const page = await fetchAdminPublicPage(id);
            setForm(pageToForm(page));
        } catch (requestError) {
            setError(requestError?.response?.data?.message || requestError.message);
        } finally {
            setPageLoading(false);
        }
    };

    const savePage = async () => {
        if (!form) return;

        setSaving(true);
        setMessage(null);
        setError(null);

        try {
            const payload = {
                pageKey: form.pageKey,
                path: form.path,
                slug: form.slug,
                lang: form.lang,
                textMode: form.textMode,
                type: form.type,
                status: form.status,
                seoTitle: form.seoTitle,
                seoDescription: form.seoDescription,
                canonical: form.canonical,
                image: form.image,
                sourceFile: form.sourceFile,
                metadata: parseJson("Metadata JSON", form.metadataText, {}),
                faqItems: parseJson("FAQ JSON", form.faqText, []),
                links: parseJson("Links JSON", form.linksText, []),
                blocks: form.blocks.map((block, index) => ({
                    id: block.id,
                    sortOrder: Number(block.sortOrder) || index + 1,
                    type: block.type,
                    format: block.format,
                    status: block.status,
                    html: block.html,
                    markdown: block.markdown,
                    payload: parseJson(`Block ${index + 1} Payload JSON`, block.payloadText, {}),
                })),
            };

            const savedPage = await updateAdminPublicPage(form.id, payload);
            setForm(pageToForm(savedPage));
            setMessage("Public page saved.");
            await loadList();
        } catch (saveError) {
            setError(saveError?.response?.data?.message || saveError.message);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        loadList();
    }, [loadList]);

    return (
        <div className="py-2">
            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
            {message && <Alert variant="success" className="py-2 small">{message}</Alert>}

            <Row className="g-3">
                <Col xl={4} lg={5}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Row className="g-2 mb-2">
                                <Col md={4}>
                                    <Form.Select size="sm" value={filters.lang} onChange={(event) => updateFilter("lang", event.target.value)}>
                                        <option value="all">All locales</option>
                                        <option value="en">en</option>
                                        <option value="uk">uk</option>
                                        <option value="ru">ru</option>
                                    </Form.Select>
                                </Col>
                                <Col md={4}>
                                    <Form.Select size="sm" value={filters.type} onChange={(event) => updateFilter("type", event.target.value)}>
                                        {typeOptions.map((type) => (
                                            <option key={type} value={type}>{type === "all" ? "All types" : type}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={4}>
                                    <Form.Select size="sm" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
                                        <option value="all">Any status</option>
                                        {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={5}>
                                    <Form.Control
                                        size="sm"
                                        placeholder="slug, title, path"
                                        value={filters.q}
                                        onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") loadList();
                                        }}
                                    />
                                </Col>
                                <Col md="auto">
                                    <Button size="sm" variant="success" onClick={loadList}>Apply</Button>
                                </Col>
                                <Col md={3}>
                                    <Form.Select size="sm" value={filters.limit} onChange={(event) => updateFilter("limit", Number(event.target.value))}>
                                        {[25, 50, 100, 200].map((limit) => <option key={limit} value={limit}>{limit}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col className="d-flex gap-2 justify-content-end">
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={filters.page <= 1}
                                        onClick={() => updateFilter("page", filters.page - 1)}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={filters.page >= listData.totalPages}
                                        onClick={() => updateFilter("page", filters.page + 1)}
                                    >
                                        Next
                                    </Button>
                                </Col>
                            </Row>

                            <div className="text-muted small mb-2">
                                {listLoading ? "Loading pages..." : `${listData.items.length} of ${listData.total} pages loaded.`}
                            </div>

                            <div className="border rounded" style={{ maxHeight: "70vh", overflow: "auto" }}>
                                {listData.items.map((page) => (
                                    <button
                                        key={page.id}
                                        type="button"
                                        className={`w-100 text-start border-0 border-bottom px-2 py-1 ${selectedId === page.id ? "bg-success bg-opacity-10" : "bg-white"}`}
                                        onClick={() => loadPage(page.id)}
                                    >
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span className="small text-truncate">{compactPageLabel(page)}</span>
                                            <Badge bg={page.status === "PUBLISHED" ? "success" : "secondary"} className="flex-shrink-0"   style={{ fontSize: "0.6rem" }}>
                                                {page.status}
                                            </Badge>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={8} lg={7}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            {!form && (
                                <div className="text-muted py-5 text-center">
                                    {pageLoading ? <Spinner animation="border" /> : "Select a public page from the database."}
                                </div>
                            )}

                            {form && (
                                <>
                                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                                        <div>
                                            <h4 className="mb-1">{form.path} ({form.lang})</h4>
                                            <div className="text-muted small">ID {form.id} · updated {form.updatedAt ? new Date(form.updatedAt).toLocaleString() : "unknown"}</div>
                                        </div>
                                        <Button size="sm" variant="success" disabled={saving || pageLoading || validationIssues.length > 0} onClick={savePage}>
                                            {saving ? "Saving..." : "Save"}
                                        </Button>
                                    </div>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Path</Form.Label>
                                                <Form.Control size="sm" value={form.path} onChange={(event) => updateForm("path", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Lang</Form.Label>
                                                <Form.Control size="sm" value={form.lang} onChange={(event) => updateForm("lang", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select size="sm" value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
                                                    {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Title</Form.Label>
                                                <Form.Control size="sm" value={form.seoTitle} onChange={(event) => updateForm("seoTitle", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Type</Form.Label>
                                                <Form.Control size="sm" value={form.type} onChange={(event) => updateForm("type", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Page key</Form.Label>
                                                <Form.Control size="sm" value={form.pageKey} onChange={(event) => updateForm("pageKey", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label>Description</Form.Label>
                                                <Form.Control size="sm" as="textarea" rows={3} value={form.seoDescription} onChange={(event) => updateForm("seoDescription", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Canonical</Form.Label>
                                                <Form.Control size="sm" value={form.canonical} onChange={(event) => updateForm("canonical", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Image</Form.Label>
                                                <Form.Control size="sm" value={form.image} onChange={(event) => updateForm("image", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Slug</Form.Label>
                                                <Form.Control size="sm" value={form.slug} onChange={(event) => updateForm("slug", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Text mode</Form.Label>
                                                <Form.Control size="sm" value={form.textMode} onChange={(event) => updateForm("textMode", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label>Source file</Form.Label>
                                                <Form.Control size="sm" value={form.sourceFile} onChange={(event) => updateForm("sourceFile", event.target.value)} />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <JsonTextarea label="Metadata JSON" value={form.metadataText} onChange={(value) => updateForm("metadataText", value)} rows={6} />

                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h5 className="mb-0">Content Blocks</h5>
                                        <Button size="sm" variant="outline-success" onClick={addBlock}>Add block</Button>
                                    </div>

                                    {form.blocks.map((block, index) => (
                                        <Card className="mb-3" key={block.id || `new-${index}`}>
                                            <Card.Header className="d-flex justify-content-between align-items-center">
                                                <strong>Block #{index + 1}</strong>
                                                <Button size="sm" variant="outline-danger" onClick={() => removeBlock(index)}>Remove</Button>
                                            </Card.Header>
                                            <Card.Body>
                                                <Row className="g-2 mb-3">
                                                    <Col md={2}>
                                                        <Form.Label>Order</Form.Label>
                                                        <Form.Control size="sm" type="number" value={block.sortOrder} onChange={(event) => updateBlock(index, "sortOrder", event.target.value)} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Label>Type</Form.Label>
                                                        <Form.Control size="sm" value={block.type} onChange={(event) => updateBlock(index, "type", event.target.value)} />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Label>Format</Form.Label>
                                                        <Form.Select size="sm" value={block.format} onChange={(event) => updateBlock(index, "format", event.target.value)}>
                                                            {FORMAT_OPTIONS.map((format) => <option key={format} value={format}>{format}</option>)}
                                                        </Form.Select>
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Label>Status</Form.Label>
                                                        <Form.Select size="sm" value={block.status} onChange={(event) => updateBlock(index, "status", event.target.value)}>
                                                            {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                                                        </Form.Select>
                                                    </Col>
                                                </Row>

                                                {block.format === "HTML" && (
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">
                                                            {block.type === "rich_text" ? "Content HTML" : "HTML"}
                                                        </Form.Label>
                                                        <HtmlEditor value={block.html} onChange={(value) => updateBlock(index, "html", value)} />
                                                    </Form.Group>
                                                )}

                                                {block.format === "MARKDOWN" && (
                                                    <Form.Control
                                                        size="sm"
                                                        as="textarea"
                                                        rows={12}
                                                        value={block.markdown}
                                                        onChange={(event) => updateBlock(index, "markdown", event.target.value)}
                                                    />
                                                )}

                                                {block.format === "JSON" && (
                                                    <JsonTextarea
                                                        label="Payload JSON"
                                                        value={block.payloadText}
                                                        onChange={(value) => updateBlock(index, "payloadText", value)}
                                                        rows={10}
                                                    />
                                                )}
                                            </Card.Body>
                                        </Card>
                                    ))}

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <JsonTextarea label="FAQ JSON" value={form.faqText} onChange={(value) => updateForm("faqText", value)} rows={10} />
                                        </Col>
                                        <Col md={6}>
                                            <JsonTextarea label="Outbound Links JSON" value={form.linksText} onChange={(value) => updateForm("linksText", value)} rows={10} />
                                        </Col>
                                    </Row>

                                    <div className="mt-2">
                                        <h5>Validation</h5>
                                        {validationIssues.length ? (
                                            <Alert variant="warning" className="py-2 small mb-0">
                                                {validationIssues.map((issue) => (
                                                    <div key={issue}>{issue}</div>
                                                ))}
                                            </Alert>
                                        ) : (
                                            <Alert variant="light" className="border py-2 small mb-0">
                                                No validation issues
                                            </Alert>
                                        )}
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PublicPageEditorAdmin;
